import { LineSeries, LineSvgLayer, ResponsiveLine } from "@nivo/line";
import {
    differenceInCalendarMonths,
    eachDayOfInterval,
    format,
    max,
    min,
    parseISO,
    startOfDay,
    Interval,
    isSameDay,
    isSameMonth,
    eachMonthOfInterval,
} from "date-fns";
import { CSSProperties, FC, useMemo, useState } from "react";
import { usePrint } from "app/hook/use-print";
import { ReportVersionListFieldsFragment } from "app/api/graph/types";
import { useChartTheme } from "app/hook/use-chart-theme";

type Props = {
    versions: ReportVersionListFieldsFragment[];
    style?: CSSProperties;
    dateFrom?: Date;
    dateTo?: Date;
};

type Datum = {
    x: string;
    y: number;
};

type Item = {
    id: string;
    color: string;
    data: Datum[];
};

const margin = { top: 10, right: 10, bottom: 50, left: 50 };

enum TickPeriod {
    Day,
    Month,
}

const calculateDateInterval = (versions: ReportVersionListFieldsFragment[]): Interval => {
    const dates = versions
        .map((version) => version.publishedAt)
        .filter((publishedAt): publishedAt is string => publishedAt != null)
        .map((d) => startOfDay(parseISO(d)));

    return {
        start: min(dates),
        end: max(dates),
    };
};

const getDefaultTickPeriod = (versions: ReportVersionListFieldsFragment[]): TickPeriod => {
    const interval = calculateDateInterval(versions);
    return differenceInCalendarMonths(interval.end, interval.start) < 1 ? TickPeriod.Day : TickPeriod.Month;
};

const parseData = (versions: ReportVersionListFieldsFragment[], tickPeriod: TickPeriod): Item[] => {
    const interval = calculateDateInterval(versions);
    const range = tickPeriod === TickPeriod.Month ? eachMonthOfInterval(interval) : eachDayOfInterval(interval);

    return [
        {
            id: "Published",
            color: "#c00000",
            data: range
                .map(
                    (date): Datum => ({
                        x: tickPeriod === TickPeriod.Month ? format(date, "yyyy-MM-01") : format(date, "yyyy-MM-dd"),
                        // Count the number of versions that were published on this date
                        y: versions.filter((version) => {
                            if (version.publishedAt == null) {
                                return false;
                            }

                            const datePublished = startOfDay(parseISO(version.publishedAt));

                            return tickPeriod === TickPeriod.Month
                                ? isSameMonth(date, datePublished)
                                : isSameDay(date, datePublished);
                        }).length,
                    }),
                )
                .filter(({ y }) => y > 0),
        },
    ];
};

const styleById: Record<string, CSSProperties> = {
    default: {
        strokeWidth: 1,
    },
};

const customLines: LineSvgLayer<LineSeries> = ({ series, lineGenerator, xScale, yScale }) => {
    return series.map(({ id, data, color }) => (
        <path
            key={id}
            d={
                lineGenerator(
                    data.map((d) => ({
                        x: xScale(d.data.x ?? 0),
                        y: yScale(d.data.y ?? 0),
                    })),
                ) ?? undefined
            }
            fill="none"
            stroke={color}
            style={styleById[id] || styleById.default}
        />
    ));
};

export const ReportVersionTimelineChart: FC<Props> = ({ versions, style, dateFrom, dateTo }) => {
    const theme = useChartTheme();
    const defaultTickPeriod = useMemo(() => getDefaultTickPeriod(versions), [versions]);
    const [tickPeriod, setTickPeriod] = useState<TickPeriod>(defaultTickPeriod);
    const data = useMemo(() => parseData(versions, tickPeriod), [versions, tickPeriod]);
    const { isPrint } = usePrint();

    return (
        <div style={{ height: "300px", ...style }} className="d-flex flex-column">
            <div className="text-end">
                <select value={tickPeriod} onChange={(e) => setTickPeriod(parseInt(e.target.value))}>
                    <option value={TickPeriod.Day}>Day</option>
                    <option value={TickPeriod.Month}>Month</option>
                </select>
            </div>
            <ResponsiveLine
                data={data}
                margin={margin}
                animate={!isPrint}
                colors={{ datum: "color" }}
                xFormat={tickPeriod === TickPeriod.Month ? "time:%Y-%m" : "time:%Y-%m-%d"}
                xScale={{
                    format: "%Y-%m-%d",
                    precision: tickPeriod === TickPeriod.Month ? "month" : "day",
                    type: "time",
                    useUTC: false,
                    min: dateFrom !== undefined ? format(dateFrom, "yyyy-MM-dd") : undefined,
                    max: dateTo !== undefined ? format(dateTo, "yyyy-MM-dd") : undefined,
                }}
                yScale={{ type: "linear", min: 0, max: "auto", stacked: false, reverse: false }}
                axisTop={null}
                axisRight={null}
                axisBottom={{
                    tickRotation: -45,
                    format: tickPeriod === TickPeriod.Month ? "%b" : "%b %d",
                    tickValues: 5,
                }}
                axisLeft={{
                    tickValues: 5,
                    format: (e: unknown) => (Number.isInteger(e) ? e : ""),
                }}
                gridYValues={5}
                pointSize={10}
                pointColor={{ theme: "background" }}
                pointBorderWidth={2}
                pointBorderColor={{ from: "serieColor" }}
                pointLabelYOffset={-12}
                useMesh={true}
                legends={[]}
                layers={[
                    "grid",
                    "markers",
                    "axes",
                    "areas",
                    "crosshair",
                    customLines,
                    "points",
                    "slices",
                    "mesh",
                    "legends",
                ]}
                theme={theme}
            />
        </div>
    );
};
